import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../../services/api';
import AgGridTable from '../../components/AgGridTable';
import ShortUrlManagementPage from './ShortUrlManagementPage';
import CaseAuditLogsPage from './CaseAuditLogsPage';
import CaseNotesPage from './CaseNotesPage';
import SidebarPanel from '../../components/admin/SidebarPanel';
import AlertMessage from '../../components/shared/alertMessage';

const CasesPage = () => {
  const navigate = useNavigate();
  const { caseId: caseIdFromUrl } = useParams();
  const gridRef = useRef();
  const [error, setError] = useState(null);
  const [casesData, setCasesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidePanel, setSidePanel] = useState({
    isOpen: false,
    type: null, // 'linkManagement', 'auditLogs', or 'notes'
    caseId: null
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [alertState, setAlertState] = useState({ show: false, type: '', title: '', message: '' });

  
    const fetchCases = async (options = {}) => {
      // Capture gridRef at function start to avoid closure issues
      const currentGridRef = gridRef;
      
      try {
        const params = { page_size: 10000, ...options };
        
        // Only set loading for full refreshes, not partial updates
        if (!options.fields) {
          setLoading(true);
        }
        
        const response = await adminApi.getCases(params);
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          if (options.fields) {
            // Smart refresh: compare fresh data with current grid data
            if (currentGridRef.current && currentGridRef.current.api) {
              const freshData = response.data.data;
              const currentRows = [];
              currentGridRef.current.api.forEachNode(node => currentRows.push(node.data));
              
              // Create maps for efficient lookup
              const currentMap = new Map(currentRows.map(row => [row.CoreServer_CaseID, row]));
              const freshMap = new Map(freshData.map(row => [row.CoreServer_CaseID, row]));
              
              const toAdd = [];
              const toUpdate = [];
              const toRemove = [];
              
              // Find new and updated cases
              freshData.forEach(freshRow => {
                const currentRow = currentMap.get(freshRow.CoreServer_CaseID);
                if (!currentRow) {
                  // New case - trigger full reload
                  toAdd.push(freshRow);
                } else {
                  // Check if any requested fields changed
                  const requestedFields = options.fields.split(',').map(f => f.trim());
                  const hasChanges = requestedFields.some(field => 
                    currentRow[field] !== freshRow[field]
                  );
                  if (hasChanges) {
                    // Merge fresh data into existing row data (preserve other columns)
                    const mergedRow = { ...currentRow };
                    requestedFields.forEach(field => {
                      mergedRow[field] = freshRow[field];
                    });
                    toUpdate.push(mergedRow);
                  }
                }
              });
              
              // Find removed cases
              currentRows.forEach(currentRow => {
                if (!freshMap.has(currentRow.CoreServer_CaseID)) {
                  toRemove.push(currentRow);
                }
              });
              
              // Handle changes
              if (toAdd.length > 0 || toRemove.length > 0) {
                // New or removed cases detected - do full reload
                fetchCases();
                return;
              }
              // Only updates - apply transaction
              if (toUpdate.length > 0) {
                currentGridRef.current.api.applyTransaction({
                  update: toUpdate
                });
                
                // Force refresh of affected columns
                const requestedFields = options.fields.split(',').map(f => f.trim());
                currentGridRef.current.api.refreshCells({ 
                  columns: requestedFields,
                  force: true 
                });
              }
            }
          } else {
            // Replace all cases data for full fetch
            setCasesData(response.data.data);
          }
        } else {
          setError(response.data?.message || 'Failed to fetch cases or invalid data received');
        }
      } catch (err) {
        setError(err.message || 'An error occurred while fetching cases');
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      fetchCases();
    }, []); // Initial load only

  // Handle URL parameter - automatically open sidebar if caseId is in URL
  useEffect(() => {
    if (caseIdFromUrl && casesData.length > 0 && !sidePanel.isOpen) {
      const caseData = casesData.find(c => c.id === caseIdFromUrl);
      if (caseData) {
        // Determine the type from the URL path
        const path = window.location.pathname;
        if (path.includes('/audit-logs')) {
          handleAuditLogsClick(caseData);
        } else if (path.includes('/notes')) {
          handleNotesClick(caseData);
        } else if (path.includes('/link-management')) {
          handleShortUrlClick(null, caseData);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseIdFromUrl, casesData, sidePanel.isOpen]);

  // Function to refresh case statuses and link-related fields
  const refreshCaseStatuses = () => {
    fetchCases({ fields: 'CoreServer_CaseID,mira_sol_case_status,updated_at,short_url,short_url_expiration_time,shorturl_access_attempts' });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Copy URL to clipboard
  const copyUrlToClipboard = (url) => {
    navigator.clipboard.writeText(url)
      .then(() => {
        // URL copied to clipboard
      })
      .catch(err => {
        console.error('Failed to copy URL: ', err);
      });
  };

  // Copy Case ID to clipboard
  const copyCaseIdToClipboard = (caseId) => {
    navigator.clipboard.writeText(caseId)
      .then(() => {
        // Case ID copied to clipboard
      })
      .catch(err => {
        console.error('Failed to copy Case ID: ', err);
      });
  };

  // Get status badge class based on status
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'initiated':
        return 'badge bg-info';
      case 'short_url_sent':
        return 'badge bg-info';
      case 'short_url_accessed':
        return 'badge bg-info';
      case 'yob_validated':
        return 'badge bg-info';
      case 'token_generated':
        return 'badge bg-info';
      case 'patient_checkin_initiated':
        return 'badge bg-primary';
      case 'patient_declined_video':
        return 'badge bg-warning';
      case 'patient_requested_human_support':
        return 'badge bg-warning';
      case 'device_check_complete':
        return 'badge bg-primary';
      case 'forms_started':
        return 'badge bg-primary';
      case 'forms_filled':
        return 'badge bg-primary';
      case 'forms_completed':
        return 'badge bg-primary';
      case 'insurance_started':
        return 'badge bg-primary';
      case 'insurance_verified':
        return 'badge bg-primary';
      case 'check_in_complete':
        return 'badge bg-success';
      case 'completed':
        return 'badge bg-success';
      case 'expired':
        return 'badge bg-danger';
      case 'cancelled':
        return 'badge bg-danger';
      case 'Locked - Attempts exceeded':
        return 'badge bg-warning';
      case 'unlocked_by_admin':
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  };

  // Format status for display
  const formatStatus = (status) => {
    switch (status) {
      case 'initiated': return 'Initiated';
      case 'short_url_sent': return 'Short URL Sent';
      case 'short_url_accessed': return 'Short URL Accessed';
      case 'yob_validated': return 'YOB Validated';
      case 'token_generated': return 'Token Generated';
      case 'patient_checkin_initiated': return 'Patient Check-in Started';
      case 'patient_declined_video': return 'Patient Declined Video';
      case 'patient_requested_human_support': return 'Patient Requested Human Support';
      case 'device_check_complete': return 'Device Check Complete';
      case 'forms_started': return 'Forms Started';
      case 'forms_filled': return 'Forms Filled';
      case 'forms_completed': return 'Forms Completed';
      case 'insurance_started': return 'Insurance Started';
      case 'insurance_verified': return 'Insurance Verified';
      case 'check_in_complete': return 'Check-in Complete';
      case 'expired': return 'Expired';
      case 'cancelled': return 'Cancelled';
      case 'Locked - Attempts exceeded': return 'Locked - Attempts Exceeded';
      case 'unlocked_by_admin': return 'Unlocked by Admin';
      default:
        return status;
    }
  };

  const handleShortUrlClick = async (event, caseData) => { // Accept event object
     const isLockedOrExpired = caseData.mira_sol_case_status === 'Locked - Attempts exceeded' || (caseData.short_url_expiration_time && new Date(caseData.short_url_expiration_time) < new Date());

     setSidePanel({ isOpen: true, type: 'linkManagement', caseId: caseData.id });
     // Navigate to URL with case ID
     navigate(`/admin/cases/${caseData.id}/link-management`);

     if (!isLockedOrExpired) {
        // Only copy the short URL to clipboard if it's not locked/expired
        if (caseData.short_url) {
           try {
              await navigator.clipboard.writeText(caseData.short_url);
              if (event && event.target) { // Check if event and target exist
                event.target.textContent = 'Link copied';
              }
           } catch (err) {
              return null
           }
        }
     }
  };

  const handleAuditLogsClick = (caseData) => {
    setSidePanel({
      isOpen: true,
      type: 'auditLogs',
      caseId: caseData.id
    });
    // Navigate to URL with case ID
    navigate(`/admin/cases/${caseData.id}/audit-logs`);
  };

  const handleNotesClick = (caseData) => {
    setSidePanel({
      isOpen: true,
      type: 'notes',
      caseId: caseData.id
    });
    // Navigate to URL with case ID
    navigate(`/admin/cases/${caseData.id}/notes`);
  };

  // Function to update a specific case's data in the grid
  const updateCaseDataInGrid = (updatedCase) => {
    setCasesData(prevData => {
      const newData = prevData.map(caseItem =>
        caseItem.id === updatedCase.id ? updatedCase : caseItem
      );
      // Use ag-Grid API to refresh the specific row
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.applyTransaction({ update: [updatedCase] });
      }
      return newData;
    });
  };

  const handleCloseSidePanel = () => {
    setSidePanel({
      isOpen: false,
      type: null,
      caseId: null
    });
    // Navigate back to base URL to remove the ID from URL
    navigate('/admin/cases');
  };

  // useEffect to manage the 'open' class on the sidebar panel
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar-panel');
    if (sidebar) {
      if (sidePanel.isOpen) {
        sidebar.classList.add('open');
      } else {
        sidebar.classList.remove('open');
      }
    }
  }, [sidePanel.isOpen]);

  // useEffect to handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown')) {
        setDropdownOpen(false);
      }};
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Custom Cell Renderer for Actions column
  const ActionsCellRenderer = (props) => {
    const { data } = props;
    if (!data) return null;

    const isLockedOrExpired = data.mira_sol_case_status === 'Locked - Attempts exceeded' || (data.short_url_expiration_time && new Date(data.short_url_expiration_time) < new Date());

    return (
      <div className="btn-group btn-group-small">
        {data.short_url && (
          <>
            {isLockedOrExpired ? (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={(event) => props.handleShortUrlClick(event, data)}
                title="Disconnected Link - Click to Manage"
              >
                <i className="fas fa-unlink"></i> Reset Link
              </button>
            ) : (
              <button
                className="btn btn-sm btn-outline-success"
                onClick={(event) => props.handleShortUrlClick(event, data)}
                title="Copy Check-in URL"
              >
                <i className="fas fa-link"></i> Manage Link
              </button>
            )}
          </>
        )}
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => props.handleNotesClick(data)}
          title="View Case Notes"
        >
          <i className="fas fa-file-medical"></i> Notes
        </button>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => props.handleAuditLogsClick(data)}
          title="View Audit Logs"
        >
          <i className="fas fa-history"></i> Audit Trail
        </button>
      </div>
    );
  };

  // Custom Cell Renderer for Status column
  const StatusCellRenderer = (props) => {
    const { value } = props;
    return (
      <span className={getStatusBadgeClass(value)}>
        {formatStatus(value)}
      </span>
    );
  };

  // Custom Cell Renderer for Patient column
  const PatientCellRenderer = (props) => {
    const { data } = props;
    if (!data || !data.patient_info_fk) return 'N/A';
    return (
      <Link to={`/admin/patients/${data.patient_info_fk.patient_id}`} className="text-body">
        {`${data.patient_info_fk.first_name} ${data.patient_info_fk.last_name} (${data.patient_info_fk.dob})`}
      </Link>
    );
  };

  // Custom Cell Renderer for Provider column
  const ProviderCellRenderer = (props) => {
    const { data } = props;
    if (!data || !data.provider_info_fk) return 'N/A';
    return (
      <Link to={`/admin/providers/${data.provider_info_fk.provider_id}`} className="text-body">
        {`${data.provider_info_fk.first_name} ${data.provider_info_fk.last_name} (${data.provider_info_fk.specialties?.map(s => s.name).filter(Boolean).join(', ') || 'N/A'})`}
      </Link>
    );
  };

  // Custom Cell Renderer for Health System column
  const HealthSystemCellRenderer = (props) => {
    const { data } = props;
    if (!data || !data.healthcare_info_fk) return 'N/A';
    return (
      <Link to={`/admin/health-systems/${data.healthcare_info_fk.health_system_id}`} className="text-body">
        {data.healthcare_info_fk.name}
      </Link>
    );
  };

  // Custom Cell Renderer for Zoom Session Topic (Passcode)
  const ZoomSessionCellRenderer = (props) => {
    const { data } = props;
    if (!data || !data.latest_zoom_meeting) return 'No Zoom Meeting requested';
    return (
      <>
        {data.latest_zoom_meeting.topic.length > 20
          ? `${data.latest_zoom_meeting.topic.substring(0, 20)}...`
          : data.latest_zoom_meeting.topic}
        <button
          className="btn btn-sm btn-link p-0"
          onClick={() => copyCaseIdToClipboard(data.latest_zoom_meeting.topic)}
          title="Copy Zoom Topic"
        >
          <i className="fas fa-copy"></i>
        </button>
        &nbsp;({data.latest_zoom_meeting.passcode})
        <button
          className="btn btn-sm btn-link p-0"
          onClick={() => copyCaseIdToClipboard(data.latest_zoom_meeting.passcode)}
          title="Copy Zoom Passcode"
        >
          <i className="fas fa-copy"></i>
        </button>
      </>
    );
  };

  // Layout saving callbacks - memoized to prevent unnecessary re-renders
  const handleLayoutSaved = useCallback(() => {
    setAlertState({ show: true, type: 'success', title: '',
      message: 'Table layout has been saved to your preferences'
    });
  }, []);

  const handleLayoutReset = useCallback(() => {
    setAlertState({ show: true, type: 'info', title: '',
      message: 'Table layout has been reset to your saved preferences'
    });
  }, []);

  // ag-Grid Column Definitions - memoized to prevent grid reinitialization
  const columnDefs = useMemo(() => [
    {
      headerName: 'Enterprise Case ID',
      field: 'CoreServer_CaseID',
      cellRenderer: (props) => (
        <>
          <span className="text-truncate" style={{ maxWidth: '150px' }}>
            {props.value}
          </span>
          <button
            className="btn btn-sm btn-link p-0"
            onClick={() => copyCaseIdToClipboard(props.value)}
            title="Copy Enterprise Case ID"
          >
            <i className="fas fa-copy"></i>
          </button>
        </>
      )
    },
    {
      headerName: 'Patient',
      field: 'patient_info_fk',
      filter: true,
      cellRenderer: PatientCellRenderer,
      valueFormatter: (params) => {
        if (!params.value) return 'N/A';
        return `${params.value.first_name || ''} ${params.value.last_name || ''}`.trim() || 'N/A';
      }
    },
    {
      headerName: 'Provider (Specialty)',
      field: 'provider_info_fk',
      cellRenderer: ProviderCellRenderer,
      valueFormatter: (params) => {
        if (!params.value) return 'N/A';
        return `${params.value.first_name || ''} ${params.value.last_name || ''}`.trim() || 'N/A';
      }
    },
    {
      headerName: 'Health System',
      field: 'healthcare_info_fk',
      cellRenderer: HealthSystemCellRenderer,
      valueFormatter: (params) => {
        if (!params.value) return 'N/A';
        return params.value.name || 'N/A';
      }
    },
    {
      headerName: 'Status',
      field: 'mira_sol_case_status',
      cellRenderer: StatusCellRenderer
    },
    {
      headerName: 'Zoom Session Topic (Passcode)',
      field: 'latest_zoom_meeting',
      filter: false,
      cellRenderer: ZoomSessionCellRenderer,
    },
    {
      headerName: 'Created',
      field: 'created_at',
      valueFormatter: p => formatDate(p.value)
    },
    {
      headerName: 'Updated',
      field: 'updated_at',
      valueFormatter: p => formatDate(p.value)
    },
    {
      headerName: 'Actions',
      field: 'actions',
      filter: true,
      pinned: 'right',
      // maxWidth: 200,
      cellRenderer: (props) => <ActionsCellRenderer {...props} handleShortUrlClick={handleShortUrlClick} handleAuditLogsClick={handleAuditLogsClick} handleNotesClick={handleNotesClick} />,
      valueFormatter: (params) => {
        const data = params.data;
        if (!data) return '';
        const isLockedOrExpired = data.mira_sol_case_status === 'Locked - Attempts exceeded' || (data.short_url_expiration_time && new Date(data.short_url_expiration_time) < new Date());
        if (data.short_url) {
          return isLockedOrExpired ? 'Inactive Link' : 'Active Link';
        }
        return 'No Short URL';
      }
    }
  ], []);

  // Default column definition - memoized to prevent grid reinitialization
  const defaultColDef = useMemo(() => ({
    minWidth: 100,
    resizable: true,
    sortable: true,
    filter: true,
  }), []);

  return (
    <>
      {/* Alert Message */}
      {alertState.show && (
        <AlertMessage
          show={alertState.show}
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          onClose={() => setAlertState({ ...alertState, show: false })}
        />
      )}

      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Case Management</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><span>Admin</span></li>
                <li className="breadcrumb-item active">Cases</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      
      <div className="d-flex justify-content-end align-items-center mb-4">
        <div className="d-flex align-items-center me-2">
          <small className="text-muted me-2">
            {!loading && `Updated ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}`}
          </small>
          <button className="btn btn-secondary btn-sm" onClick={refreshCaseStatuses} disabled={loading} >
            {loading ? ( <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            ) : ( <i className="fas fa-sync-alt me-1"></i> )}
            {loading ? 'Refreshing...' : 'Refresh Status'}
          </button>
        </div>
        <Link to="/admin/initiate-visit" className="btn btn-primary btn-sm">
          <i className="fas fa-plus-circle me-1"></i> Initiate Visit
        </Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="card">
          <div className="card-body text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
            <AgGridTable
              ref={gridRef}
              rowData={casesData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={params => params.data.CoreServer_CaseID}
              enableLayoutSaving={true}
              gridId="cases_grid"
              showLayoutControls={true}
              controlsPosition="top"
              onLayoutSaved={handleLayoutSaved}
              onLayoutReset={handleLayoutReset}
            />
        </div>
      )}

      {/* Consolidated Side Panel */}
      <SidebarPanel
        isOpen={sidePanel.isOpen}
        onClose={handleCloseSidePanel}
        title={
          sidePanel.type === 'linkManagement' ? 'Patient Link Management' :
          sidePanel.type === 'auditLogs' ? 'Case Audit Trail' :
          sidePanel.type === 'notes' ? 'Case Notes' :
          ''
        }
      >
        {sidePanel.caseId && sidePanel.type === 'linkManagement' && (
          <ShortUrlManagementPage caseId={sidePanel.caseId} onCaseUpdated={updateCaseDataInGrid} />
        )}
        {sidePanel.caseId && sidePanel.type === 'auditLogs' && (
          <CaseAuditLogsPage caseId={sidePanel.caseId} />
        )}
        {sidePanel.caseId && sidePanel.type === 'notes' && (
          <CaseNotesPage caseId={sidePanel.caseId} />
        )}
      </SidebarPanel>
    </>
  );
};

export default CasesPage;
